/**
 * BE 側 paymentService は `property_section_id` パラメータを
 * 内部で unit_id として扱い `paymentRepository.findByUnit` (UnitPaymentIndex GSI)
 * 経由で取得するよう改修済。FE は `propertySectionId` 引数に **unit_id** を渡せば
 * そのまま BE が正しく Unit ベースの入金履歴を返す。
 *
 * URL は移行期の互換のため `/property-sections/:id/payments` のままとする。
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";

const BaseURL = import.meta.env.VITE_API_URL;
const propertyPaymentRequests = new Map<string, Promise<unknown>>();

export const fetchPaymentsBySection = createAsyncThunk<
  any,
  { propertySectionId: string; status?: string; payment_item?: string } | string
>("payments/fetchBySection", async (arg, { rejectWithValue }) => {
  try {
    const params = typeof arg === "string" ? { propertySectionId: arg } : arg;
    const { propertySectionId, status, payment_item } = params;
    const query: Record<string, string> = {};
    if (status) query.status = status;
    if (payment_item) query.payment_item = payment_item;
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/property-sections/${propertySectionId}/payments`,
      { params: query },
    );
    return res.data.data;
  } catch (error: any) {
    return rejectWithValue(error.response?.data || "Failed to fetch payments");
  }
});

/**
 * 入金単発取得 (payment_id 指定)。
 *
 * BE に GET /payments/:id は無く、Payment は Unit (property_section_id) で
 * partition されている。よって propertySectionId を必須引数で受け取り、
 * セクション内の一覧から client side で payment_id 一致を探す方式。
 *
 * /payments/:id ページの直リン/リロード時にも動くよう、URL querystring に
 * `property_section_id` を必ず付ける運用とセット (PaymentDetailDrawer / 一覧の navigate 参照)。
 */
/**
 * 物件全体の入金一覧 (収支タブ「収入」サブタブの物件全体ビュー用)。
 * BE: GET /properties/:property_id/payments (paymentRepository.findByProperty)。
 * Unit 単位の fetchPaymentsBySection とは別 state (propertyList) に格納する。
 */
export const fetchPaymentsByProperty = createAsyncThunk<
  any,
  { propertyId: string }
>("payments/fetchByProperty", async ({ propertyId }, { rejectWithValue }) => {
  try {
    let request = propertyPaymentRequests.get(propertyId);
    if (!request) {
      request = ipponApi
        .get(`${BaseURL}/api/v1/properties/${propertyId}/payments`)
        .then((res) => res.data.data);
      propertyPaymentRequests.set(propertyId, request);
      void request
        .finally(() => {
          if (propertyPaymentRequests.get(propertyId) === request) {
            propertyPaymentRequests.delete(propertyId);
          }
        })
        .catch(() => {});
    }
    return await request;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || "Failed to fetch property payments",
    );
  }
});

export const fetchPaymentById = createAsyncThunk<
  any,
  { propertySectionId: string; paymentId: string; propertyId?: string }
>(
  "payments/fetchById",
  async ({ propertySectionId, paymentId, propertyId }, { rejectWithValue }) => {
    const pickFromList = (payload: any): any | undefined => {
      const list: any[] = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      return list.find((p) => p.payment_id === paymentId);
    };
    try {
      // まず Unit (section) 紐付けの入金一覧から探す
      const res = await ipponApi.get(
        `${BaseURL}/api/v1/property-sections/${propertySectionId}/payments`,
      );
      let found = pickFromList(res.data?.data ?? {});
      // 見つからない & propertyId あり → 「その他」収入 (Unit 非紐付け) は
      // UnitPaymentIndex に乗らないため物件全体の入金から探す (収入サブタブ用)
      if (!found && propertyId) {
        const res2 = await ipponApi.get(
          `${BaseURL}/api/v1/properties/${propertyId}/payments`,
        );
        found = pickFromList(res2.data?.data ?? {});
      }
      if (!found) {
        return rejectWithValue({
          errorType: "notFound",
          message: "支払いが見つかりません",
        });
      }
      return found;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch payment");
    }
  },
);

export const createPayment = createAsyncThunk<
  any,
  {
    propertySectionId: string;
    property_id: string;
    status: string;
    items: Array<{ category_id: string; name: string; amount: number }>;
    title?: string;
    aggregation_month?: string;
    scheduled_date?: string;
    transfer_datetime?: string;
    transfer_method?: string;
    remarks?: string;
  }
>(
  "payments/create",
  async ({ propertySectionId, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/property-sections/${propertySectionId}/payments`,
        body,
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to create payment",
      );
    }
  },
);

/**
 * 物件直下の入金作成 (Unit 非紐付け = 「その他」収入)。
 * BE: POST /properties/:property_id/payments (§7-B 案B)。
 */
export const createPropertyPayment = createAsyncThunk<
  any,
  {
    property_id: string;
    status: string;
    items: Array<{ category_id: string; name: string; amount: number }>;
    title?: string;
    aggregation_month?: string;
    scheduled_date?: string;
    transfer_datetime?: string;
    transfer_method?: string;
    remarks?: string;
  }
>(
  "payments/createProperty",
  async ({ property_id, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(
        `${BaseURL}/api/v1/properties/${property_id}/payments`,
        body,
      );
      return res.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to create property payment",
      );
    }
  },
);

export const updatePayment = createAsyncThunk<
  any,
  {
    propertySectionId: string;
    paymentId: string;
    sort_key: string;
    status?: string;
    items?: Array<{ category_id: string; name: string; amount: number }>;
    title?: string;
    aggregation_month?: string;
    scheduled_date?: string;
    transfer_datetime?: string;
    transfer_method?: string;
    remarks?: string;
  }
>(
  "payments/update",
  async ({ propertySectionId, paymentId, ...body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.patch(
        `${BaseURL}/api/v1/property-sections/${propertySectionId}/payments/${paymentId}`,
        body,
      );
      return { ...res.data.data, ...body, payment_id: paymentId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update payment",
      );
    }
  },
);

export const deletePayment = createAsyncThunk<
  any,
  { propertySectionId: string; paymentId: string; sort_key: string }
>(
  "payments/delete",
  async ({ propertySectionId, paymentId, sort_key }, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/property-sections/${propertySectionId}/payments/${paymentId}`,
        { data: { sort_key } },
      );
      return paymentId;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to delete payment",
      );
    }
  },
);

interface PaymentState {
  list: {
    data: any[];
    loading: boolean;
    error: string | null;
    availableStatuses: string[];
    availableItems: string[];
  };
  // 物件全体の入金一覧 (収入サブタブ用)。Unit 単位の list とは分離。
  propertyList: {
    data: any[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: PaymentState = {
  list: {
    data: [],
    loading: false,
    error: null,
    availableStatuses: [],
    availableItems: [],
  },
  propertyList: {
    data: [],
    loading: false,
    error: null,
    loaded: false,
  },
};

const paymentSlice = createSlice({
  name: "payments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentsBySection.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchPaymentsBySection.fulfilled, (state, action) => {
        state.list.loading = false;
        const payload: any = action.payload || {};
        state.list.data = payload.data || payload || [];
        if (Array.isArray(payload.available_statuses)) {
          state.list.availableStatuses = payload.available_statuses;
        }
        if (Array.isArray(payload.available_items)) {
          state.list.availableItems = payload.available_items;
        }
      })
      .addCase(fetchPaymentsBySection.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = action.payload as string;
      })
      .addCase(createPayment.fulfilled, (state, action) => {
        state.list.data = [action.payload, ...state.list.data];
      })
      .addCase(updatePayment.fulfilled, (state, action) => {
        const updated = action.payload;
        if (updated?.payment_id) {
          state.list.data = state.list.data.map((p) =>
            p.payment_id === updated.payment_id ? { ...p, ...updated } : p,
          );
        }
      })
      .addCase(deletePayment.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (p) => p.payment_id !== action.payload,
        );
      })
      // 物件全体入金一覧 (収入サブタブ)
      .addCase(fetchPaymentsByProperty.pending, (state) => {
        state.propertyList.loading = true;
        state.propertyList.error = null;
      })
      .addCase(fetchPaymentsByProperty.fulfilled, (state, action) => {
        state.propertyList.loading = false;
        const payload: any = action.payload || {};
        state.propertyList.data = Array.isArray(payload)
          ? payload
          : payload.data || [];
        state.propertyList.loaded = true;
      })
      .addCase(fetchPaymentsByProperty.rejected, (state, action) => {
        state.propertyList.loading = false;
        state.propertyList.error = action.payload as string;
      })
      .addCase(createPropertyPayment.fulfilled, (state, action) => {
        // 作成した「その他」収入を物件全体一覧に即反映
        if (action.payload) {
          state.propertyList.data = [
            action.payload,
            ...state.propertyList.data,
          ];
        }
      });
  },
});

export default paymentSlice.reducer;
